/**
 * ForgotPasswordPage — request a password reset email.
 *
 * Always shows a generic "check your email" confirmation so the form
 * cannot be used to probe which addresses have accounts. Only
 * transport-level failures (rate limit, network) surface as errors.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { AuthCard } from '../components/AuthCard';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setLoading(false);
    if (authError) {
      setError(authError.message);
    } else {
      setSent(true);
    }
  };

  return (
    <AuthCard>
      {sent ? (
        <div className="text-center py-4">
          <div
            className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--primary-soft)' }}
          >
            <svg className="w-6 h-6" style={{ color: 'var(--primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-sm font-semibold" style={{ color: '#1F1E26' }}>
            Check your email
          </p>
          <p className="text-sm mt-1" style={{ color: '#6B6A75' }}>
            If an account exists for{' '}
            <span className="font-medium" style={{ color: '#1F1E26' }}>{email}</span>,
            we sent a link to reset your password.
          </p>
          <Link
            to="/login"
            className="inline-block mt-4 text-xs underline"
            style={{ color: '#8E8CB3' }}
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="text-lg font-semibold" style={{ color: '#1F1E26' }}>
              Reset your password
            </p>
            <p className="text-sm mt-1" style={{ color: '#6B6A75' }}>
              Enter your email address and we&apos;ll send you a link to set a new password.
            </p>
          </div>

          <div>
            <label htmlFor="email" className="public-label">
              Email address
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="public-input"
            />
          </div>

          {error && (
            <div className="public-error">
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !email}
            className="public-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending...' : 'Send reset link'}
          </button>

          <p className="text-center">
            <Link
              to="/login"
              className="text-xs underline"
              style={{ color: '#8E8CB3' }}
            >
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthCard>
  );
}
